import { MIS_3005_D_SIZING, RULES_BUNDLE_VERSION, outdoorUnitById } from "@sts/rules-data";
import { checkOutdoorUnits, type OutdoorUnitCheck } from "./design-check-outdoor-unit.js";
import { checkRooms, type DesignWaterTemps, type RoomsCheck } from "./design-check-rooms.js";
import {
  formatC,
  formatKw,
  formatPercent,
  formatW,
  type CheckState,
  type DesignCheckContext,
  type DesignCheckInput,
  type DesignCheckLine,
} from "./design-check-types.js";
import { ENGINE_VERSION } from "./heat-loss.js";
import {
  catalogueUnitsMeeting,
  datasheetCapacityAt,
  type CapacityPrefill,
  type SizedCatalogueUnit,
} from "./heat-pump-capacity.js";
import { checkSizing } from "./sizing.js";
import type { PlanObjectOnPlan } from "./plan-geometry.js";
import type { CalcStamp } from "./types.js";

/**
 * The MCS design check on the floor plan — asked for by Josh on 2026-09-10:
 *
 *   "this design must take care of actual room heat loss and house required
 *   heat-pump recommendation, it must turn red if we recommend smaller
 *   radiator that does not fit according to mcs requirements, design should
 *   recommend heat-pump size according to mcs, based on the particular
 *   heat-pump system must also calculate sound db calculation and size of hp
 *   for the wall where we are installing"
 *
 * One pure function of the survey's heat loss, the designer's design
 * decisions and what is drawn on the plan, run in three places from this one
 * copy: live on the portal's plan as things are moved, in the API
 * (`GET /api/surveys/:id/design-check`), and — for the installer pack — by
 * whatever prints it.
 *
 *   ROOMS (`design-check-rooms.ts`)   radiators vs room heat loss at the design
 *                                     water temperatures; bigger sizes that fit;
 *                                     the worst room, MIS 3005-D §3.8.1(d).
 *   HEAT PUMP (here)                  the design heat load the unit must meet
 *                                     at ≥100% (MIS 3005-D §3.4.1(e), via
 *                                     `checkSizing`), the placed unit's
 *                                     capacity against it, and the catalogue
 *                                     units whose datasheet output meets it.
 *   OUTDOOR UNIT (`…-outdoor-unit.ts`) its wall, the manufacturer's clearances,
 *                                     MCS 020 a) directivity and noise at the
 *                                     neighbour's window.
 *
 * Nothing here invents a figure: every number is the engine's, a sourced
 * catalogue's (labelled, and provisional where the catalogue is), or the
 * designer's own input (labelled). Where one is missing the line is grey and
 * says what to enter.
 */

/** The flow-minus-return the design pack assumes (`design-pack-builder.ts`) until the designer states one. */
export const DESIGN_PACK_SYSTEM_DELTA_T_K = 5;

export interface HeatPumpUnitSizing {
  readonly objectId: string;
  readonly name: string;
  readonly state: CheckState;
  readonly message: string;
  /** "7 kW — the size on the box, not a sizing figure" */
  readonly nominal?: string;
  readonly capacityW?: number;
  readonly capacitySource?: string;
  readonly fraction?: number;
  /** A datasheet figure at the design conditions the designer may accept. */
  readonly prefill?: CapacityPrefill;
  /** Why no datasheet figure is offered, when none is. */
  readonly prefillUnavailable?: string;
  readonly notes: readonly string[];
}

export interface HeatPumpSizingCheck {
  readonly state: CheckState;
  /** The design heat load, W — the whole-house heat loss. */
  readonly requiredW?: number;
  readonly outdoorC?: number;
  readonly flowC?: number;
  /** "Minimum heat pump output needed: 7.4 kW at −3 °C outside and 45 °C flow, excluding any back-up heater (MIS 3005-D §3.4.1(e))." */
  readonly message: string;
  readonly hotWaterNote: string;
  readonly units: readonly HeatPumpUnitSizing[];
  /** Catalogue units whose DATASHEET output at the design conditions meets the load, smallest first. */
  readonly recommendations: readonly SizedCatalogueUnit[];
  readonly recommendationsNote?: string;
  readonly notes: readonly string[];
}

export interface DesignCheckResult {
  readonly context: DesignCheckContext;
  readonly temps?: DesignWaterTemps;
  readonly flow: { readonly state: CheckState; readonly message: string };
  readonly heatLoss: { readonly state: CheckState; readonly message: string; readonly totalW?: number };
  readonly rooms: RoomsCheck;
  readonly heatPump: HeatPumpSizingCheck;
  readonly outdoorUnits: readonly OutdoorUnitCheck[];
  /** The compact block, one line per topic, worst news first within each. */
  readonly lines: readonly DesignCheckLine[];
  readonly stamp: CalcStamp;
}

function tempsOf(context: DesignCheckContext): DesignWaterTemps | undefined {
  if (context.designFlowTempC === null) return undefined;
  const deltaTAssumed = context.systemDeltaTK === null;
  const systemDeltaTK = context.systemDeltaTK ?? DESIGN_PACK_SYSTEM_DELTA_T_K;
  return {
    flowC: context.designFlowTempC,
    returnC: context.designFlowTempC - systemDeltaTK,
    systemDeltaTK,
    deltaTAssumed,
  };
}

// -------------------------------------------------------------- heat pump --

function checkHeatPump(
  context: DesignCheckContext,
  objects: readonly PlanObjectOnPlan[],
): HeatPumpSizingCheck {
  const hotWaterNote =
    "Space heating only: MIS 3005-D §3.4.1(e) sizes the heat pump against the room-by-room heat load; hot water is checked separately (cylinder and recovery, §3.5).";
  const units = objects.filter(
    (o): o is Extract<PlanObjectOnPlan, { kind: "heat-pump" }> => o.kind === "heat-pump",
  );
  const notes: string[] = [];
  const outdoorC = context.externalDesignTempC;
  const flowC = context.designFlowTempC;

  if (!context.heatLoss.available) {
    return {
      state: "unknown",
      message: `Heat pump size cannot be worked out: no heat loss — ${context.heatLoss.reason}`,
      hotWaterNote,
      units: units.map((u) => ({
        objectId: u.id,
        name: u.spec.name,
        state: "unknown",
        message: `${u.spec.name}: nothing to size it against yet.`,
        notes: [],
      })),
      recommendations: [],
      notes,
    };
  }
  const requiredW = context.heatLoss.totalHeatLossW;
  if (outdoorC === null || flowC === null) {
    return {
      state: "unknown",
      requiredW,
      ...(outdoorC !== null ? { outdoorC } : {}),
      message:
        `Design heat load ${formatKw(requiredW)}. ` +
        (flowC === null
          ? "Set the design flow temperature: the heat pump's output depends on it, so the minimum size is stated at a flow temperature."
          : "The survey has no design external temperature."),
      hotWaterNote,
      units: units.map((u) => ({
        objectId: u.id,
        name: u.spec.name,
        state: "unknown",
        message: `${u.spec.name}: set the design flow temperature to check it.`,
        notes: [],
      })),
      recommendations: [],
      notes,
    };
  }

  const message =
    `Minimum heat pump output needed: ${formatKw(requiredW, 1)} (${formatW(requiredW)}) at ${formatC(outdoorC)} outside and ` +
    `${formatC(flowC)} flow, excluding any back-up heater (MIS 3005-D §3.4.1(e)).`;

  if (context.systemType === "hybrid") {
    notes.push(
      "This is a hybrid design: MIS 3005-D §3.4.2 applies instead (heat pump ≥55% of the load rated at 55 °C flow, combined ≥100%). Check it on the Heat pump selection screen.",
    );
  }
  if (flowC > MIS_3005_D_SIZING.data.alternativeDesignFlowTempThresholdC) {
    notes.push(
      `Design flow ${formatC(flowC)} is above ${MIS_3005_D_SIZING.data.alternativeDesignFlowTempThresholdC} °C: MIS 3005-D §3.4.5 requires an alternative design at or below it to be offered.`,
    );
  }

  const sized: HeatPumpUnitSizing[] = units.map((unit) => {
    const catalogue = unit.spec.catalogueId ? outdoorUnitById(unit.spec.catalogueId) : undefined;
    const nominal = catalogue ? `${catalogue.sizeLabel} — the size on the box, not a sizing figure` : undefined;
    const lookup = datasheetCapacityAt(unit.spec.catalogueId, outdoorC, flowC);
    const prefillPart = lookup.found ? { prefill: lookup.prefill } : { prefillUnavailable: lookup.reason };
    const design = unit.spec.design;
    const capacity = design?.capacityAtDesignW;
    const unitNotes: string[] = [];
    if (capacity === undefined) {
      return {
        objectId: unit.id,
        name: unit.spec.name,
        state: "unknown" as const,
        message:
          `${unit.spec.name}: enter its output at ${formatC(outdoorC)} / ${formatC(flowC)} flow` +
          (lookup.found ? " — or use the datasheet figure offered." : " from the MCS Product Directory or the datasheet."),
        ...(nominal ? { nominal } : {}),
        ...prefillPart,
        notes: unitNotes,
      };
    }
    const conditions = design?.capacityConditions;
    if (!conditions || conditions.outdoorC !== outdoorC || conditions.flowC !== flowC) {
      return {
        objectId: unit.id,
        name: unit.spec.name,
        state: "unknown" as const,
        message: conditions
          ? `${unit.spec.name}: its ${formatW(capacity)} was entered for ${formatC(conditions.outdoorC)} / ${formatC(conditions.flowC)} flow, not today's ${formatC(outdoorC)} / ${formatC(flowC)} — enter its output at these conditions.`
          : `${unit.spec.name}: ${formatW(capacity)} was entered without the conditions it applies at — re-enter it for ${formatC(outdoorC)} / ${formatC(flowC)} flow.`,
        capacityW: capacity,
        ...(design?.capacitySource ? { capacitySource: design.capacitySource } : {}),
        ...(nominal ? { nominal } : {}),
        ...prefillPart,
        notes: unitNotes,
      };
    }
    const sizing = checkSizing({
      designHeatLoadW: requiredW,
      heatPumpCapacityAtDesignW: capacity,
      designFlowTempC: flowC,
      systemKind: "monovalent",
      complianceMode: false,
      calculationDate: context.calculationDate,
    });
    const fraction = sizing.capacityFraction;
    const meets = sizing.meetsCapacityRule;
    if (context.systemType === "hybrid") {
      // §3.4.2, not §3.4.1(e), governs a hybrid: the heat pump alone need not
      // cover 100%. Stated, not judged here.
      return {
        objectId: unit.id,
        name: unit.spec.name,
        state: "unknown" as const,
        message:
          `${unit.spec.name}: ${formatW(capacity)} at ${formatC(outdoorC)} / ${formatC(flowC)} flow (${formatPercent(fraction, { failing: !meets })} of ${formatW(requiredW)}). ` +
          "A hybrid is sized by MIS 3005-D §3.4.2 (heat pump ≥55% of the load rated at 55 °C, combined ≥100%) — check it on the Heat pump selection screen.",
        capacityW: capacity,
        ...(design?.capacitySource ? { capacitySource: design.capacitySource } : {}),
        fraction,
        ...(nominal ? { nominal } : {}),
        ...prefillPart,
        notes: unitNotes,
      };
    }
    return {
      objectId: unit.id,
      name: unit.spec.name,
      state: meets ? ("pass" as const) : ("fail" as const),
      message:
        `${unit.spec.name}: ${formatW(capacity)} at ${formatC(outdoorC)} / ${formatC(flowC)} flow against ${formatW(requiredW)} — ` +
        `${formatPercent(fraction, { failing: !meets })}. ${meets ? "Meets" : "Below"} the 100% MIS 3005-D §3.4.1(e) asks for` +
        (meets ? "." : ` — ${formatW(requiredW - capacity)} short.`),
      capacityW: capacity,
      ...(design?.capacitySource ? { capacitySource: design.capacitySource } : {}),
      fraction,
      ...(nominal ? { nominal } : {}),
      ...prefillPart,
      notes: unitNotes,
    };
  });

  if (units.length > 1) {
    notes.push(
      `${units.length} outdoor units are on the plan; each is checked against the whole load on its own. If they share the load, add their outputs and check the total.`,
    );
  }

  const found = catalogueUnitsMeeting(requiredW, outdoorC, flowC);
  const recommendations = found.meeting.slice(0, 3);
  const recommendationsNote =
    `From manufacturer datasheets at ${formatC(outdoorC)} / ${formatC(flowC)} flow — confirm in the MCS Product Directory. ` +
    `${found.notAssessable} of the plan's ${found.notAssessable + found.meeting.length + found.tooSmall} catalogue units have no usable datasheet output at these conditions and are not ranked` +
    (found.tooSmall > 0 ? `; ${found.tooSmall} are too small.` : ".");

  const states = sized.map((s) => s.state);
  const state: CheckState =
    units.length === 0
      ? "unknown"
      : states.includes("fail")
        ? "fail"
        : states.includes("unknown")
          ? "unknown"
          : "pass";
  return {
    state,
    requiredW,
    outdoorC,
    flowC,
    message,
    hotWaterNote,
    units: sized,
    recommendations,
    recommendationsNote,
    notes,
  };
}

// ------------------------------------------------------------ the whole --

const worst = (states: readonly CheckState[]): CheckState =>
  states.includes("fail")
    ? "fail"
    : states.includes("unknown")
      ? "unknown"
      : states.includes("warn")
        ? "warn"
        : "pass";

export function computeDesignCheck(input: DesignCheckInput): DesignCheckResult {
  const { context, rooms, objects } = input;
  const temps = tempsOf(context);

  const flow: DesignCheckResult["flow"] = temps
    ? {
        state: temps.deltaTAssumed ? "warn" : "pass",
        message:
          `Design flow ${formatC(temps.flowC)}, return ${formatC(temps.returnC)} ` +
          (temps.deltaTAssumed
            ? `(a ${temps.systemDeltaTK} K difference is assumed — the design pack's figure — until you set yours).`
            : `(your ${temps.systemDeltaTK} K flow–return difference).`),
      }
    : {
        state: "unknown",
        message:
          "Design flow temperature not set. Radiators and the heat pump are checked at it, so nothing is assumed — set it here.",
      };

  const heatLoss: DesignCheckResult["heatLoss"] = context.heatLoss.available
    ? {
        state: "pass",
        totalW: context.heatLoss.totalHeatLossW,
        message:
          `Heat loss ${formatKw(context.heatLoss.totalHeatLossW)} for ${context.heatLoss.rooms.length} room${context.heatLoss.rooms.length === 1 ? "" : "s"}` +
          (context.externalDesignTempC !== null ? ` at ${formatC(context.externalDesignTempC)} outside` : "") +
          " (BS EN 12831, MIS 3005-D §3.4.1(a)).",
      }
    : { state: "unknown", message: `No heat loss: ${context.heatLoss.reason}` };

  const roomsCheck = checkRooms({ rooms, objects, heatLoss: context.heatLoss, temps });
  const heatPump = checkHeatPump(context, objects);
  const outdoorUnits = checkOutdoorUnits({
    rooms,
    objects,
    jurisdiction: context.jurisdiction,
    calculationDate: context.calculationDate,
  });

  const lines: DesignCheckLine[] = [];
  lines.push({ key: "heat-loss", state: heatLoss.state, text: heatLoss.message });
  lines.push({
    key: "heat-pump-needed",
    state: heatPump.requiredW !== undefined && heatPump.flowC !== undefined ? "pass" : "unknown",
    text:
      heatPump.requiredW !== undefined && heatPump.flowC !== undefined && heatPump.outdoorC !== undefined
        ? `Heat pump needed: at least ${formatKw(heatPump.requiredW, 1)} at ${formatC(heatPump.outdoorC)} / ${formatC(heatPump.flowC)} flow (≥100%, no back-up heater).`
        : heatPump.message,
  });
  if (heatPump.units.length === 0) {
    lines.push({
      key: "heat-pump-chosen",
      state: "unknown",
      text:
        heatPump.recommendations.length > 0
          ? `No outdoor unit on the plan. Datasheet sizes that meet it: ${heatPump.recommendations.map((r) => `${r.name} (${formatW(r.prefill.capacityW)})`).join(", ")}.`
          : "No outdoor unit on the plan yet.",
    });
  } else {
    for (const unit of heatPump.units) {
      lines.push({ key: "heat-pump-chosen", state: unit.state, text: unit.message, objectId: unit.objectId });
    }
  }

  const assessable = roomsCheck.rooms.filter((r) => r.state === "pass" || r.state === "fail");
  const short = roomsCheck.rooms.filter((r) => r.state === "fail");
  const greyRooms = roomsCheck.rooms.filter((r) => r.state === "unknown");
  const radiatorsText =
    short.length > 0
      ? `Radiators: ${short.length} room${short.length === 1 ? "" : "s"} short — ${short.map((r) => `${r.name} ${r.fraction !== undefined ? formatPercent(r.fraction, { failing: true }) : ""}`.trim()).join(", ")}.`
      : assessable.length > 0
        ? `Radiators: all ${assessable.length} room${assessable.length === 1 ? "" : "s"} with radiators and a heat loss are covered.`
        : "Radiators: none can be checked yet.";
  const greyText =
    greyRooms.length > 0 ? ` ${greyRooms.length} room${greyRooms.length === 1 ? "" : "s"} not assessed (see each).` : "";
  const firstShort = short[0];
  lines.push({
    key: "radiators",
    state: short.length > 0 ? "fail" : assessable.length > 0 ? (greyRooms.length > 0 ? "warn" : "pass") : "unknown",
    text: radiatorsText + greyText,
    ...(firstShort ? { room: { story: firstShort.story, index: firstShort.index } } : {}),
  });

  for (const unit of outdoorUnits) {
    lines.push({ key: "noise", state: unit.noise.state, text: `Noise: ${unit.noise.message}`, objectId: unit.objectId });
    const placementState = worst([unit.wall.state, unit.clearances.state]);
    const placementText =
      placementState === "pass"
        ? `Placement: clearances clear on the plan (${unit.clearances.source === "none" ? "no figures" : "manufacturer's figures"}).`
        : `Placement: ${[unit.wall, ...unit.clearances.items].filter((i) => i.state !== "pass").map((i) => i.message)[0] ?? (unit.clearances.openings[0]?.message ?? unit.clearances.items[0]?.message ?? "")}`;
    lines.push({ key: "placement", state: placementState, text: placementText, objectId: unit.objectId });
  }

  return {
    context,
    ...(temps ? { temps } : {}),
    flow,
    heatLoss,
    rooms: roomsCheck,
    heatPump,
    outdoorUnits,
    lines,
    stamp: {
      engineVersion: ENGINE_VERSION,
      rulesBundleVersion: RULES_BUNDLE_VERSION,
      calculatedAt: context.calculationDate,
      complianceMode: false,
    },
  };
}
